import { useEffect } from 'react'
import { useLang } from '../i18n'
import { WeappsWordmark, LoadingOverlay } from '@weapps/design'

// Centered-card auth shell in the grow-mobile design language (ADR 0097): warm surface, the weapps
// wordmark, the shared type scale, auto light/dark. Shared by every auth screen (sign-in, confirm, reset).
export function AuthLayout({ title, subtitle, children }: { title: string; subtitle?: string; children: React.ReactNode }) {
  const { t } = useLang()
  // Keyboard-aware (#248): the card is vertically centered, so on a phone the on-screen keyboard can cover
  // the focused field. When a field gains focus, scroll it into view once the keyboard has opened (the delay
  // lets the viewport settle first). No-op on desktop where the field is already visible.
  useEffect(() => {
    const onFocusIn = (e: FocusEvent) => {
      const el = e.target as HTMLElement | null
      if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA')) {
        setTimeout(() => el.scrollIntoView({ block: 'center', behavior: 'smooth' }), 300)
      }
    }
    document.addEventListener('focusin', onFocusIn)
    return () => document.removeEventListener('focusin', onFocusIn)
  }, [])
  return (
    <div
      className="flex min-h-screen flex-col items-center justify-center px-6 py-10"
      style={{ background: 'var(--scr)' }}>
      <WeappsWordmark className="mb-8 h-8 w-auto" style={{ color: 'var(--txt)' }} />
      <div
        className="w-full max-w-sm rounded-3xl border p-7"
        style={{
          background: 'var(--srf)',
          borderColor: 'rgba(var(--brd), 0.1)',
          boxShadow: '0 18px 44px rgba(40, 25, 10, 0.08)',
        }}>
        <div className="mb-5 text-center">
          <h1 className="t-title-xl" style={{ color: 'var(--txt)' }}>
            {title}
          </h1>
          {subtitle && (
            <p className="t-body mt-1.5" style={{ color: 'var(--txt2)' }}>
              {subtitle}
            </p>
          )}
        </div>
        <div className="flex flex-col gap-4">{children}</div>
      </div>
      <p className="t-caption mt-6" style={{ color: 'var(--txt3)' }}>
        {t.footer}
      </p>
    </div>
  )
}

// Full-screen branded waiting state (the breathing weapps logo) shared by sign-in and the federated
// callback, so the user sees a clear "working…" screen during the auth round-trip.
export function AuthLoading({ title, subtitle }: { title: string; subtitle?: string }) {
  return (
    <div className="min-h-screen" style={{ background: 'var(--scr)' }}>
      <LoadingOverlay label={subtitle ?? title} />
    </div>
  )
}

// Shared pill social button in the grow-mobile design language. Children are the icon + label.
